import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback, useRef, useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  HeadlineS,
  HeadlineXL,
} from '@sorare/core/src/atoms/typography';
import type { GraphQLResult } from '@sorare/core/src/components/form/Form';
import { ComposeTeam } from '@sorare/core/src/components/noCardEntry/ComposeTeam';
import { NoCardEntryForm } from '@sorare/core/src/components/noCardEntry/NoCardEntryForm';
import type { GoogleReCAPTCHA } from '@sorare/core/src/components/recaptcha';
import { ReCAPTCHA } from '@sorare/core/src/components/recaptcha';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useQueryString } from '@sorare/core/src/hooks/useQueryString';
import { useUploadWithPresign } from '@sorare/core/src/hooks/useUploadWithPresign';

import { Header } from '../Header';
import type {
  So5NoCardRoutePickConfigsQuery,
  So5NoCardRoutePickConfigsQueryVariables,
} from './__generated__/index.graphql';
import { useAcceptNoCardDraftableAppearance } from './useAcceptNoCardDraftableAppearance';
import { useGenerateSo5NoCardLineup as useGenerateNoCardLineup } from './useGenerateSo5NoCardLineup';
import { useRequestNoCardDraftableAppearance } from './useRequestNoCardDraftableAppearance';
import { useResetNoCardDraftedAppearance as useResetNoCardDraftedAppearances } from './useResetNoCardDraftedAppearances';

const CenteredContainer = styled(Vertical).attrs({ gap: 0, center: true })`
  margin-top: var(--triple-unit);
  width: 100%;
  justify-content: flex-start;
`;

interface Props {
  so5FixtureId: string;
  disclaimers: MessageDescriptor[];
}

const NO_CARD_ROUTE_PICK_CONFIGS_QUERY = gql`
  query So5NoCardRoutePickConfigsQuery($so5FixtureId: ID!) {
    so5 {
      id
      so5Fixture(id: $so5FixtureId) {
        slug
        noCardRoutePickConfigs {
          id
          ...NoCardEntryComposeTeam_so5NoCardRouteFixturePickConfig
        }
      }
    }
  }
  ${ComposeTeam.fragments.so5NoCardRouteFixturePickConfig}
` as TypedDocumentNode<
  So5NoCardRoutePickConfigsQuery,
  So5NoCardRoutePickConfigsQueryVariables
>;

const messages = defineMessages({
  composeTeam: {
    id: 'NoCardEntry.RequestLineupForm.composeTeam',
    defaultMessage: 'Compose team',
  },
});

export const RequestLineupForm = ({ so5FixtureId, disclaimers }: Props) => {
  const recaptchaRef = useRef<GoogleReCAPTCHA>(null);
  const accessToken = useQueryString('token') || '';
  const version = useQueryString('version');
  const [teamCompleted, setTeamCompleted] = useState(version === '1');
  const [success, setSuccess] = useState(false);
  const [showComposeTeam, setShowComposeTeam] = useState(false);

  const { data: configData } = useQuery<
    So5NoCardRoutePickConfigsQuery,
    So5NoCardRoutePickConfigsQueryVariables
  >(NO_CARD_ROUTE_PICK_CONFIGS_QUERY, {
    variables: { so5FixtureId },
  });

  const generateNoCardLineup = useGenerateNoCardLineup();
  const uploadWithPresign = useUploadWithPresign();

  const requestAppearance = useRequestNoCardDraftableAppearance();
  const acceptAppearance = useAcceptNoCardDraftableAppearance();
  const resetAppearances = useResetNoCardDraftedAppearances();

  const onSubmit = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    attributes: any,
    onResult: (res: GraphQLResult) => void
  ) => {
    if (!teamCompleted) {
      setShowComposeTeam(true);
      onResult({});
    } else {
      const {
        recaptchaTokenV2,
        proofOfResidency,
      }: {
        recaptchaTokenV2: Nullable<string>;
        proofOfResidency: Nullable<File>;
      } = attributes;

      let proofOfResidencyPresignedKey;
      if (proofOfResidency) {
        proofOfResidencyPresignedKey =
          await uploadWithPresign(proofOfResidency);
        if (!proofOfResidencyPresignedKey) {
          return;
        }
      }

      const result = await generateNoCardLineup({
        so5FixtureId: `So5Fixture:${so5FixtureId}`,
        recaptchaTokenV2,
        accessToken,
        proofOfResidencyPresignedKey,
      });
      onResult(result);
    }
  };

  const requestDraftableAppearance = useCallback(
    async (configSignature: string) => {
      const data = await requestAppearance({
        so5FixtureId: `So5Fixture:${so5FixtureId}`,
        accessToken,
        configSignature,
      });

      return {
        appearance:
          data.data?.requestSo5NoCardDraftableAppearance?.draftableAppearance,
        eligibleSlot:
          data.data?.requestSo5NoCardDraftableAppearance?.eligibleSlotIndex,
      };
    },
    [requestAppearance, so5FixtureId, accessToken]
  );

  const resetDraftedAppearances = useCallback(async () => {
    await resetAppearances({
      accessToken,
      so5FixtureId: `So5Fixture:${so5FixtureId}`,
    });
  }, [resetAppearances, accessToken, so5FixtureId]);

  const acceptDraftableAppearance = async () => {
    const data = await acceptAppearance({
      accessToken,
      so5FixtureId: `So5Fixture:${so5FixtureId}`,
    });
    return data.errors.length === 0;
  };

  const onTeamCompleted = () => {
    setTeamCompleted(true);
    setShowComposeTeam(false);
  };

  if (success)
    return (
      <Vertical gap={0}>
        <CenteredContainer>
          <HeadlineXL as="h2">
            <FormattedMessage
              id="NoCardEntryForm.successTitle"
              defaultMessage="Thank you"
            />
          </HeadlineXL>
          <HeadlineS as="h5">
            {version === '1' ? (
              <FormattedMessage
                id="NoCardEntryForm.successSubtitle"
                defaultMessage="You will soon receive an email with a proposed team"
              />
            ) : (
              <FormattedMessage
                id="NoCardEntryForm.composeTeam.successSubtitle"
                defaultMessage="You will soon receive an email with eligible competitions"
              />
            )}
          </HeadlineS>
        </CenteredContainer>
      </Vertical>
    );

  return (
    <Vertical gap={0}>
      <Header />
      <NoCardEntryForm
        onSubmit={onSubmit}
        onSuccess={() => setSuccess(teamCompleted)}
        submitMessage={
          version === '2' && !teamCompleted ? messages.composeTeam : undefined
        }
        information={
          version === '2' && teamCompleted ? (
            <Horizontal>
              <BodyM color="var(--c-onBase-success)">
                <FormattedMessage
                  id="NoCardEntry.teamCompleted"
                  defaultMessage="Team completed"
                />
              </BodyM>
              <Button size="compact" onClick={() => setShowComposeTeam(true)}>
                Edit
              </Button>
            </Horizontal>
          ) : undefined
        }
      />
      {version === '2' && (
        <ComposeTeam
          open={showComposeTeam}
          onClose={() => setShowComposeTeam(false)}
          onSubmit={onTeamCompleted}
          requestAppearance={requestDraftableAppearance}
          resetAppearances={resetDraftedAppearances}
          acceptAppearance={acceptDraftableAppearance}
          disclaimers={disclaimers}
          configs={configData?.so5?.so5Fixture?.noCardRoutePickConfigs || []}
        />
      )}
      <ReCAPTCHA ref={recaptchaRef} />
    </Vertical>
  );
};
